/**
 * The routing module is responsible for choreographing route enter and exit actions
 * within the store. This module is a little different than the others as it uses
 * direct dispatching (as opposed to mutation commits) far more than other modules.
 *
 * This is to avoid recursive commits by explicitly delegating initial dispatches
 * to other modules where the mutation activity is more appropriate
 *
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 */
// import Vue from 'vue'
import {
	VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE,
	VUEX_ROUTING_ENTER_CALENDAR_ITEM,
	VUEX_ROUTING_ENTER_CALENDAR_ITEMS,
	VUEX_ROUTING_ENTER_DOC,
	VUEX_ROUTING_ENTER_DOCS,
	VUEX_ROUTING_ENTER_EVENT,
	VUEX_ROUTING_ENTER_EVENTS,
	VUEX_ROUTING_ENTER_JOB,
	VUEX_ROUTING_ENTER_JOB_ROUTING_BOARD,
	VUEX_ROUTING_ENTER_JOBS,
	VUEX_ROUTING_ENTER_PREMEDIA_SEARCH,
	VUEX_ROUTING_ENTER_LOGIN,
	VUEX_ROUTING_ENTER_MEMO,
	VUEX_ROUTING_ENTER_MEMOS,
	VUEX_ROUTING_ENTER_ONBOARDING_REQUEST_ACCESS,
	VUEX_ROUTING_ENTER_ONBOARDING_VERIFY_EMAIL,
	VUEX_ROUTING_ENTER_REQUESTS,
	VUEX_ROUTING_ENTER_RESET_PASSWORD,
	VUEX_ROUTING_ENTER_ROUTE,
	VUEX_ROUTING_ENTER_ROUTE_SUCCESS,
	VUEX_ROUTING_ENTER_TOOLS_ALLOCATION,
	VUEX_ROUTING_ENTER_UNAUTHENTICATED_ROUTE,
	VUEX_ROUTING_ENTER_USER_CREATE,
	VUEX_ROUTING_ENTER_USER_REQUEST_ACCESS,
	VUEX_ROUTING_ENTER_USER_UPDATE,
	VUEX_ROUTING_ENTER_USERS,
	VUEX_ROUTING_EXIT_CALENDAR_ITEM,
	VUEX_ROUTING_EXIT_DOC,
	VUEX_ROUTING_EXIT_EVENT,
	VUEX_ROUTING_EXIT_JOB,
	VUEX_ROUTING_EXIT_JOBS,
    VUEX_ROUTING_EXIT_REQUEST,
	VUEX_ROUTING_EXIT_MEMO,
	VUEX_ROUTING_EXIT_USER,
    VUEX_ROUTING_EXIT_USER_DIRECTORY,
	VUEX_ROUTING_SET_PREVIOUS_ENVIRONMENT,
	VUEX_ROUTING_UNSET_PREVIOUS_ENVIRONMENT,
	VUEX_ROUTING_ENTER_ORGANIZATION_DIRECTORY,
	VUEX_ROUTING_ENTER_CALENDAR,
	VUEX_ROUTING_PUSH_ROUTE_NAME,
	
	VUEX_ROUTING_ENTER_WIP
} from '@/store/constants/routing'
import {
	VUEX_UI_SHOW_RIGHT_DRAWER,
	VUEX_UI_SHOW_MOBILE_DRAWER_RIGHT,
	VUEX_MEMO_ITEM_MOBILE_DRAWER_INIT,
    VUEX_UI_HIDE_RIGHT_DRAWER,
	VUEX_UI_HIDE_MOBILE_DRAWER_RIGHT,
	VUEX_UI_HIDE_NAVIGATION_DRAWER
	} from '@/store/constants/ui'
import {
    VUEX_JOBS_EXIT_JOB,
    VUEX_JOBS_EXIT_JOBS,
    VUEX_JOBS_EXIT_REQUEST,
	VUEX_JOBS_GET_REQUEST,
	VUEX_JOB_GET_REQUEST,
	VUEX_JOBS_CLEAR_FILTERS,
    VUEX_JOBS_RESET_FILTERS,
	VUEX_JOBS_SET_APPROVED,
	VUEX_JOBS_GET_PROGRESSES_REQUEST,
	} from '@/store/constants/jobs'
import {
	VUEX_MEMOS_GET_REQUEST,
	VUEX_MEMO_GET_REQUEST,
	VUEX_MEMOS_CLEAR_FILTERS,
	VUEX_MEMOS_SET_APPROVED,
	VUEX_MEMO_PAGINATION_EXIT
	} from '@/store/constants/memos'
import {
	VUEX_CALENDAR_ITEM_MOBILE_DRAWER_INIT,
	VUEX_CALENDAR_ITEM_GET_REQUEST,
	VUEX_CALENDAR_ITEMS_CLEAR_FILTERS,
	VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST,
	VUEX_CALENDAR_ITEMS_GET_REQUEST,
	VUEX_CALENDAR_ITEMS_SET_APPROVED,
	VUEX_CALENDAR_ABSENCES_EXIT,
    VUEX_CALENDAR_ITEMS_ENTER_FROM_NO_RELOAD,
	} from '@/store/constants/calendarItems'
import {
	VUEX_DOCS_GET_REQUEST,
	VUEX_DOC_GET_REQUEST,
	VUEX_DOCS_CLEAR_FILTERS,
	VUEX_DOCS_SET_APPROVED,
	} from '@/store/constants/docs'
import {
	VUEX_EVENTS_GET_REQUEST,
	VUEX_EVENT_GET_REQUEST,
	VUEX_EVENTS_CLEAR_FILTERS,
	VUEX_EVENTS_SET_APPROVED,
	} from '@/store/constants/events'
import {
	VUEX_USER_CREATE_START,
	VUEX_USER_UPDATE_START,
	VUEX_USER_GET_REQUEST,
	VUEX_USERS_GET_REQUEST,
    VUEX_USERS_APPLY_DEEPSTATE,
	VUEX_USERS_GET_USERS_BY_ROLE,
    VUEX_USERS_GET_USERS_BY_ROLE_ACTIVE,
	VUEX_USERS_EMPLOYEES_PAGINATION_EXIT,
    VUEX_USERS_EXIT_USERS
} from '@/store/constants/users'
import {
	VUEX_ONBOARDING_REQUEST_ACCESS_START,
	VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST,
	VUEX_ONBOARDING_VERIFY_EMAIL_START,
} from '@/store/constants/onboarding'
import {
	VUEX_WIP_INITIALIZE
} from '@/store/constants/work_in_progress'

import router from '@/router'

import { __await } from 'tslib';


/**
 * Routing module state
 * @const
 */
const state = {
	/**
	 * We use this to retain a history of routes beyond the single previous
	 * entry in store/route added by vuex-router-sync
	 *
	 * Future Feature: For router sync and move this in there?
	 */
	routeHistory: [],

	/**
	 * If this is set, when a user navigates back in the stack, we use
	 * it to restore the state (sort, filter, etc) It is automatically
	 * disposed of if it is used or if the user does something to invalidate
	 * its relevance.
	 */
	previousEnvironment: null,
}

/**
 * Routing module getters
 * @const
 */
const	getters = {

	routeName:state => {

	}
}

/**
 * Routing module action definitions
 * @const
 */
const actions = {
    //push any route name you want
    [VUEX_ROUTING_PUSH_ROUTE_NAME]: async ({ commit }, payload) => {
        router.push({ name: payload.name }) //example 'jobs-requests'
    },


	/**
	 * Action used by the beforeEnter guard on any routes which require jobs
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_JOBS]: async ({ commit, dispatch }, extraParams) => {

        commit(VUEX_ROUTING_ENTER_JOBS)

		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
            await dispatch(VUEX_UI_HIDE_RIGHT_DRAWER)
            await commit(VUEX_UI_HIDE_MOBILE_DRAWER_RIGHT)

		})

	},


	/**
	 * Action used by the beforeEnter guard on the /jobs-manage route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_REQUESTS]: async ({ commit, dispatch }) => {

        commit(VUEX_ROUTING_ENTER_REQUESTS)
        await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
            await dispatch(VUEX_UI_HIDE_RIGHT_DRAWER)
            await commit(VUEX_UI_HIDE_MOBILE_DRAWER_RIGHT)
		})
	},


	/**
	 * Action used by the beforeEnter guard on the /jobs/view route
	 * @param {*} context
	 * @param {*} jobId
	 */
	[VUEX_ROUTING_ENTER_JOB]: async ({ commit, dispatch }, jobId) => {
		commit(VUEX_ROUTING_ENTER_JOB)
		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
			commit(VUEX_ROUTING_SET_PREVIOUS_ENVIRONMENT)
			await dispatch(VUEX_JOB_GET_REQUEST, { id:jobId, includeJobMeta:true })
		})
	},

	/**
	 * Action used by the beforeRouteLeave guard on the /jobs/view route
	 * @param {*} context
	 * @param {*} jobId
	 */
    [VUEX_ROUTING_EXIT_JOB]: async ({ commit, dispatch }) => {
        await commit(VUEX_ROUTING_UNSET_PREVIOUS_ENVIRONMENT)
        await dispatch(VUEX_JOBS_EXIT_JOB)
    },


    [VUEX_ROUTING_EXIT_JOBS]: async ({ commit, dispatch }) => {
		await commit(VUEX_ROUTING_UNSET_PREVIOUS_ENVIRONMENT)
        await dispatch(VUEX_JOBS_EXIT_JOBS,'jobs')
	},

    //exiting the request
    [VUEX_ROUTING_EXIT_REQUEST]: async ({ commit, dispatch }) => {
        await commit(VUEX_ROUTING_UNSET_PREVIOUS_ENVIRONMENT)
        await dispatch(VUEX_JOBS_EXIT_JOBS,'requests')
	},
	

	[VUEX_ROUTING_ENTER_PREMEDIA_SEARCH]: async ({commit, dispatch}) => {
		// 
	},

	/**
	 * Action used by the beforeRouteLeave guard on the /organization/Directory route
	 * @param {*} context
	 *
	 */
	[VUEX_ROUTING_EXIT_USER]: async ({ dispatch }) => {
		//await dispatch(VUEX_USERS_EMPLOYEES_PAGINATION_EXIT)
        await dispatch(VUEX_USERS_EXIT_USERS,'users')
	},
    [VUEX_ROUTING_EXIT_USER_DIRECTORY]: async ({ dispatch }) => {
        //await dispatch(VUEX_USERS_EMPLOYEES_PAGINATION_EXIT)
        await dispatch(VUEX_USERS_EXIT_USERS,'usersdirectory')
    },
	
	/**
	 * Action used by the beforeEnter guard on the /calendar_items route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_CALENDAR_ITEMS]: async ({ commit, dispatch }, params) => {
	    //reload = false is a check with the right drawer to prevent reloading when closed
	    if (params.reload != false) {
            commit(VUEX_ROUTING_ENTER_CALENDAR_ITEMS)

            await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {

            })
        } else {
            await dispatch(VUEX_CALENDAR_ITEMS_ENTER_FROM_NO_RELOAD)
        }
	},

	/**
	 * Action used by the beforeEnter guard on the /calendarItems/view route
	 * @param {*} context
	 * @param {*} calendarItemId
	 */
	[VUEX_ROUTING_ENTER_CALENDAR_ITEM]: async ({ rootState, commit, dispatch }, calendarItemId) => {
        await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
			//Note: Matt - There was code to draw the page, since page load on a deep link won't work
            //now I will detect if the page has been draw before, if now, I'll use quick filter detection
            //await commit(VUEX_CALENDAR_ITEMS_SET_APPROVED, true)
            //await dispatch(VUEX_CALENDAR_ITEMS_CLEAR_FILTERS)
			//await dispatch(VUEX_CALENDAR_ITEMS_GET_REQUEST)

            //md added, otherwise deep links produce a black right drawer
            await commit(VUEX_ROUTING_ENTER_CALENDAR_ITEM)

            //load request
			await dispatch(VUEX_CALENDAR_ITEM_GET_REQUEST, calendarItemId)

			if (rootState.mobile.mobile) {
				await commit(VUEX_CALENDAR_ITEM_MOBILE_DRAWER_INIT)
				commit(VUEX_UI_SHOW_MOBILE_DRAWER_RIGHT)
			} else {
				commit(VUEX_UI_SHOW_RIGHT_DRAWER)
			}
		})
	},

	/**
	 * Action used by the beforeEnter guard on the /calendar route
	 * @param {*} context
	 * @param {*} calendar
	 */
	[VUEX_ROUTING_ENTER_CALENDAR]: async ({ commit, dispatch }) => {
		commit(VUEX_ROUTING_ENTER_CALENDAR)

		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
			await dispatch(VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST)
		})
	},

	/**
	 * Action used by the beforeRouteLeave guard on the /calendarItems/view route
	 * @param {*} context
	 * @param {*} calendarItemId
	 */
	[VUEX_ROUTING_EXIT_CALENDAR_ITEM]: async ({ dispatch, commit }) => {
		commit(VUEX_ROUTING_UNSET_PREVIOUS_ENVIRONMENT)
		commit(VUEX_ROUTING_EXIT_CALENDAR_ITEM)
		await dispatch(VUEX_CALENDAR_ABSENCES_EXIT)
	},

	/**
	 * Action used by the beforeEnter guard on the /memos route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_MEMOS]: async ({ commit, dispatch }) => {
		commit(VUEX_ROUTING_ENTER_MEMOS)

		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
			await commit(VUEX_MEMOS_SET_APPROVED, true)
			await dispatch(VUEX_MEMOS_CLEAR_FILTERS)
			await dispatch(VUEX_MEMOS_GET_REQUEST)
		})
	},

	/**
	 * Action used by the beforeEnter guard on the /memos/view route
	 * @param {*} context
	 * @param {*} memoId
	 */
	[VUEX_ROUTING_ENTER_MEMO]: async ({ dispatch }, memoId) => {
		await dispatch(VUEX_MEMO_GET_REQUEST, memoId)
		await dispatch(VUEX_MEMO_ITEM_MOBILE_DRAWER_INIT)
	},

	/**
	 * Action used by the beforeRouteLeave guard on the /memos/view route
	 * @param {*} context
	 * @param {*} memoId
	 */
	[VUEX_ROUTING_EXIT_MEMO]: async ({ commit }) => {
		commit(VUEX_ROUTING_UNSET_PREVIOUS_ENVIRONMENT)
		commit(VUEX_ROUTING_EXIT_MEMO)
		commit(VUEX_MEMO_PAGINATION_EXIT)
	},

	/**
	 * Action used by the beforeEnter guard on the /docs route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_DOCS]: async ({ commit, dispatch }) => {
		commit(VUEX_ROUTING_ENTER_DOCS)

		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
			await commit(VUEX_DOCS_SET_APPROVED, true)
			await dispatch(VUEX_DOCS_CLEAR_FILTERS)
			await dispatch(VUEX_DOCS_GET_REQUEST)
		})
	},

	/**
	 * Action used by the beforeEnter guard on the /docs/view route
	 * @param {*} context
	 * @param {*} docId
	 */
	[VUEX_ROUTING_ENTER_DOC]: async ({ commit, dispatch }, docId) => {

	},

  /**
	 * Action used by the beforeRouteLeave guard on the /docs/view route
	 * @param {*} context
	 * @param {*} docId
	 */
	[VUEX_ROUTING_EXIT_DOC]: async ({ commit }) => {
		commit(VUEX_ROUTING_UNSET_PREVIOUS_ENVIRONMENT)
		commit(VUEX_ROUTING_EXIT_DOC)
	},


	/**
	 * Action used by the beforeEnter guard on the /events route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_EVENTS]: async ({ commit, dispatch }) => {
		commit(VUEX_ROUTING_ENTER_EVENTS)

		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
			await commit(VUEX_EVENTS_SET_APPROVED, true)
			await dispatch(VUEX_EVENTS_CLEAR_FILTERS)
			await dispatch(VUEX_EVENTS_GET_REQUEST)
		})
	},

	/**
	 * Action used by the beforeEnter guard on the /events/view route
	 * @param {*} context
	 * @param {*} eventId
	 */
	[VUEX_ROUTING_ENTER_EVENT]: async ({ commit, dispatch }, eventId) => {
		commit(VUEX_ROUTING_ENTER_EVENT)
		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
			commit(VUEX_ROUTING_SET_PREVIOUS_ENVIRONMENT)
			await dispatch(VUEX_EVENT_GET_REQUEST, eventId)
		})
	},

	/**
	 * Action used by the beforeRouteLeave guard on the /events/view route
	 * @param {*} context
	 * @param {*} eventId
	 */
	[VUEX_ROUTING_EXIT_EVENT]: async ({ commit }) => {
		commit(VUEX_ROUTING_UNSET_PREVIOUS_ENVIRONMENT)
		commit(VUEX_ROUTING_EXIT_EVENT)
	},


	/**
	 * Action used by the beforeEnter guard on the /login route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_LOGIN]: async ({ commit, dispatch }) => {
		commit(VUEX_ROUTING_ENTER_LOGIN)
		await dispatch(VUEX_ROUTING_ENTER_UNAUTHENTICATED_ROUTE)
	},


	/**
	 * Action used by the beforeEnter guard on the tools/routing route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_JOB_ROUTING_BOARD]: async ({ commit, dispatch }) => {
		commit(VUEX_ROUTING_ENTER_JOB_ROUTING_BOARD)
		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
			await dispatch(VUEX_JOBS_GET_PROGRESSES_REQUEST)
		})
	},

	/**
	 * Action used by the beforeEnter guard on the /request-access route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_ONBOARDING_REQUEST_ACCESS]: async ({ commit, dispatch }, userId) => {
		commit(VUEX_ROUTING_ENTER_ONBOARDING_REQUEST_ACCESS)

		await dispatch(VUEX_ONBOARDING_REQUEST_ACCESS_START)
	},

	/**
	 * Action used by the beforeEnter guard on the /verify-email route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_ONBOARDING_VERIFY_EMAIL]: async ({ commit, dispatch }, { userId, token }) => {
        await commit(VUEX_ROUTING_ENTER_ONBOARDING_VERIFY_EMAIL)

        await dispatch(VUEX_ONBOARDING_VERIFY_EMAIL_START)
        await dispatch(VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST, { userId, token })

	},


	/**
	 * Action used by the beforeEnter guard on the admin/users/:userId/edit route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_USER_UPDATE]: async ({ getters, commit, dispatch, rootState }, params) => {
		commit(VUEX_ROUTING_ENTER_USER_UPDATE)

		params.include = [
			'avatar',
			'permissions',
			'roles',
		]

		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE)
		await dispatch(VUEX_USER_GET_REQUEST, params)
		await dispatch(VUEX_USER_UPDATE_START, params)
	},

	/**
	 * Action used by the beforeEnter guard on the admin/users/:userId/create route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_USER_CREATE]: async ({ state, getters, commit, dispatch }, userId) => {
		commit(VUEX_ROUTING_ENTER_USER_CREATE)

		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
			await dispatch(VUEX_USER_CREATE_START)
		})
	},

	/**
	 * Action used by the beforeEnter guard on the admin/users
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_USERS]: async ({ getters, commit, dispatch }) => {
		commit(VUEX_ROUTING_ENTER_USERS)
		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {
			var deepstate = getters.getPersistence('users')
			await dispatch(VUEX_USERS_APPLY_DEEPSTATE, {'deepstate':deepstate})
		})
	},

	/**
	 * Action used by the beforeEnter guard on the /reset-password route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_RESET_PASSWORD]: async ({ commit }, params) => {
		commit(VUEX_ROUTING_ENTER_RESET_PASSWORD)
	},

	/**
	 * Action used by the beforeEnter guard on the /tools/allocation route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_TOOLS_ALLOCATION]: async ({ commit, dispatch }) => {
		commit(VUEX_ROUTING_ENTER_TOOLS_ALLOCATION)

		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {

            await dispatch(VUEX_USERS_GET_USERS_BY_ROLE_ACTIVE, SILA_ROLE_JOBS_CREATIVE)
			//await dispatch(VUEX_USERS_GET_USERS_BY_ROLE, SILA_ROLE_JOBS_CREATIVE)
			//await dispatch(VUEX_USERS_GET_USERS_BY_ROLE, SILA_ROLE_JOBS_ART_DIRECTOR)
		})
	},

	/**
	 * Action used by the beforeEnter guard on the /organization/directory route
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_ORGANIZATION_DIRECTORY]: async ({ getters, commit, dispatch }) => {
		commit(VUEX_ROUTING_ENTER_ORGANIZATION_DIRECTORY)

		await dispatch(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE).then(async () => {

			var deepstate = getters.getPersistence('usersdirectory')
			let extraParams = {'access': SILA_USER_ACCESS_TYPE_INTERNAL, 'state': SILA_USER_STATE_ACTIVE }
            await dispatch(VUEX_USERS_APPLY_DEEPSTATE,{'deepstate': deepstate, 'extraParams': extraParams})
		})

	},


	/**
	 * Dispatched on all authenticated routes beforeEnter
	 * @param {*} context
	 * @todo A check on notificaations while logged in, can perhaps remove dispatch form auth
	 */
	[VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE]: async ({ commit }) => {
		commit(VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE)
		commit(VUEX_ROUTING_ENTER_ROUTE)
	},

	/**
	 * Dispatched on all non-authenticated routes beforeEnter
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_UNAUTHENTICATED_ROUTE]:  ({ commit }) => {
		commit(VUEX_ROUTING_ENTER_UNAUTHENTICATED_ROUTE)
		commit(VUEX_ROUTING_ENTER_ROUTE)
	},


	/**
	 * Dispatched on all non-authenticated routes afterEach
	 * @param {*} context
	 */
	[VUEX_ROUTING_ENTER_ROUTE_SUCCESS]: async ({ commit }) => {
		commit(VUEX_ROUTING_ENTER_ROUTE_SUCCESS)
	},


	[VUEX_ROUTING_ENTER_WIP]:({ dispatch }) => {
		dispatch(VUEX_WIP_INITIALIZE)
		dispatch(VUEX_UI_HIDE_NAVIGATION_DRAWER)
	}

}

/**
 * Routing module mutation definitions
 * @const
 */
const mutations = {
	[VUEX_ROUTING_ENTER_AUTHENTICATED_ROUTE]: () => {},
	[VUEX_ROUTING_ENTER_JOB]: () => {},
	[VUEX_ROUTING_ENTER_JOB_ROUTING_BOARD]: () => {},
    [VUEX_ROUTING_ENTER_CALENDAR_ITEMS]: () => {},
    [VUEX_ROUTING_ENTER_JOBS]: () => {},
	[VUEX_ROUTING_ENTER_LOGIN]: () => {},
	[VUEX_ROUTING_ENTER_ONBOARDING_REQUEST_ACCESS]: () => {},
	[VUEX_ROUTING_ENTER_ONBOARDING_VERIFY_EMAIL]: () => {},
	[VUEX_ROUTING_ENTER_REQUESTS]: () => {},
	[VUEX_ROUTING_ENTER_RESET_PASSWORD]: () => {},
	[VUEX_ROUTING_ENTER_ROUTE]: () => {},
	[VUEX_ROUTING_ENTER_ROUTE_SUCCESS]: () => {},
	[VUEX_ROUTING_ENTER_TOOLS_ALLOCATION]: () => {},
	[VUEX_ROUTING_ENTER_UNAUTHENTICATED_ROUTE]: () => {},
	[VUEX_ROUTING_ENTER_USER_CREATE]: () => {},
	[VUEX_ROUTING_ENTER_USER_UPDATE]: () => {},
	[VUEX_ROUTING_ENTER_USERS]: () => {},
	[VUEX_ROUTING_EXIT_JOB]: () => {},
    [VUEX_ROUTING_EXIT_JOBS]: () => {},
    [VUEX_ROUTING_EXIT_REQUEST]: () => {},
	[VUEX_ROUTING_ENTER_USER_REQUEST_ACCESS]: () => {},
	[VUEX_ROUTING_ENTER_TOOLS_ALLOCATION]: () => {},
	[VUEX_ROUTING_ENTER_ORGANIZATION_DIRECTORY]: () => {},
	[VUEX_ROUTING_ENTER_CALENDAR]: () => {},

	/**
	 * Fired by vuex-router-sync, we latch on and push route history onto our own stack.
	 * @function
	 * @param {*} context
	 * @param {Object} payload
	 * @todo The stack could probably use a factory to manage things.
	 */
	'route/ROUTE_CHANGED': (state, payload) => {
		state.routeHistory.unshift(Object.assign(
			{},
			payload.to,
			{ matched: undefined, meta: undefined } // remove these, they cause great pain
		))

		state.routeHistory.slice(0, process.env.VUE_APP_ROUTE_HISTORY_DEPTH)
		// state.routeHistory[] = payload
	},

	[VUEX_ROUTING_SET_PREVIOUS_ENVIRONMENT]: (state, payload) => {
		state.previousEnvironment = payload
	},

	[VUEX_ROUTING_UNSET_PREVIOUS_ENVIRONMENT]: (state) => {
		state.previousEnvironment = null
	},
}

/**
 * The Routing module of our Vuex store.
 * @module store/modules/routing
 */
export default {
  state,
  getters,
  actions,
  mutations,
}
